import { ApiError } from '../identity/types';
import { centsToWire, scaled2ToWire } from './money';
import {
  AMBIGUOUS_SEED, SEED_COMPANY_RATES, SEED_RATE_DESCRIPTION, SEED_RATE_EFFECTIVE_FROM, SYSTEM_DISCIPLINES, SYSTEM_SERVICE_TYPES, type SeedRate,
} from './mockHealthcareData';
import { MOCK_NAICS_INDUSTRIES, MOCK_NAICS_SECTORS, MOCK_NAICS_VERSION } from './mockNaicsData';
import { rangesOverlap } from './mockRateResolver';
import { SECTOR_DISCIPLINES, SECTOR_SERVICE_TYPES } from './mockSectorTemplates';
import {
  FALLBACK_INDUSTRY_OPTIONS,
  HEALTHCARE_SECTOR_CODE,
  HdcErrorCode,
  INDUSTRY_DESCRIPTION_MAX,
  MAX_ADDITIONAL_INDUSTRIES,
  MAX_ALLOWED_WORK_TYPES,
  ORIGINAL_SERVICE_PAY_BASES,
  SERVICE_PAY_BASES,
  type PayBasis,
  type AllowedWorkType,
  type AllowedWorkTypes,
  type CompanyIndustry,
  type CompanyIndustryProfile,
  type CompensationSetupReview,
  type SetupReviewItem,
  type IndustryChangePreview,
  type CreateDisciplineAssignmentInput,
  type CreateServiceRateInput,
  type Discipline,
  type DisciplineAssignment,
  type DisciplineInput,
  type IndustrySelection,
  type NaicsIndustrySearch,
  type NaicsSector,
  type ServiceRate,
  type ServiceType,
  type ServiceTypeInput,
  type SetIndustryProfileInput,
} from './types';

/**
 * The in-memory data and rules behind the mock gateways: the mock stand-in for Employees (disciplines, assignments), PayrollConfig
 * (service types, industry, company rates) and Compensation (employee service rates). It enforces the contract's validation, duplicate
 * rejection, system-row read-only rule, append-only history and rate overlap guard. SYNTHETIC EXAMPLE data only (mockHealthcareData.ts).
 * The mock people and pay-run gateways read it directly, as the real services read each other over HTTP.
 */

export function problem(status: number, code: string, detail: string, extra: Record<string, unknown> = {}): ApiError {
  return new ApiError(status, detail, null, true, code, { title: code, status, detail, code, ...extra });
}

const KNOWN_INDUSTRIES = new Set(FALLBACK_INDUSTRY_OPTIONS.map((o) => o.code));
/** Every industry TAG a row may carry: the original eight plus one per NAICS sector (the server's IndustryTags). */
const KNOWN_TAGS = new Set([...KNOWN_INDUSTRIES, ...MOCK_NAICS_SECTORS.map(([code]) => `Naics${code}`)]);

/** The mock company starts as a Home health company (as before), saved as a profile in the Health Care and Social Assistance sector. */
function initialProfile(): CompanyIndustryProfile {
  const healthcareTemplates = FALLBACK_INDUSTRY_OPTIONS.filter((o) => o.code !== 'GeneralNonHealthcare').map((o) => ({ ...o }));
  return {
    primary: {
      kind: 'Chosen', sectorCode: HEALTHCARE_SECTOR_CODE, sectorTitle: 'Health Care and Social Assistance', detailCode: null, detailTitle: null, customDescription: null,
      healthcareTemplate: 'HomeHealth', healthcareTemplateLabel: 'Home health',
    },
    additional: [], naicsVersion: MOCK_NAICS_VERSION, needsReview: false, isHealthcare: true, suggestionTags: ['HomeHealth', `Naics${HEALTHCARE_SECTOR_CODE}`],
    healthcareTemplates, source: 'Profile', concurrencyVersion: 1, updatedAtUtc: null,
  };
}
const normalizeName = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
const normalizeCode = (s: string) => s.trim().toUpperCase();
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const todayIso = () => new Date().toISOString().slice(0, 10);

interface CatalogRow {
  id: string;
  code: string;
  name: string;
  description: string;
  industries: string[];
  isSystem: boolean;
  isActive: boolean;
  concurrencyVersion: number;
}

interface CatalogNames {
  noun: string; // "discipline"
  invalid: string;
  nameExists: string;
  codeExists: string;
  isSystem: string;
}

function validateCatalogInput(input: { name: string; code: string; description: string; industries: string[] }, names: CatalogNames): void {
  const fail = (detail: string) => problem(400, names.invalid, detail);
  if (!input.name.trim()) throw fail(`Enter a ${names.noun} name.`);
  if (input.name.trim().length > 100) throw fail(`The ${names.noun} name must be 100 characters or fewer.`);
  if (!input.code.trim()) throw fail(`Enter a short code for the ${names.noun}.`);
  if (input.code.trim().length > 20) throw fail('The short code must be 20 characters or fewer.');
  if ((input.description ?? '').length > 500) throw fail('The description must be 500 characters or fewer.');
  if (input.industries.some((i) => !KNOWN_TAGS.has(i))) throw fail('Choose industries from the list.');
}

function assertUnique<T extends CatalogRow>(rows: T[], input: { name: string; code: string }, names: CatalogNames, exceptId?: string): void {
  const name = normalizeName(input.name);
  const code = normalizeCode(input.code);
  for (const row of rows) {
    if (row.id === exceptId || !row.isActive) continue;
    // A company name or code may not duplicate a system default's, nor another active company row's (case-insensitive, normalised).
    if (normalizeName(row.name) === name) throw problem(409, names.nameExists, `A ${names.noun} named "${row.name}" already exists. Choose a different name, or use the existing one.`);
    if (normalizeCode(row.code) === code) throw problem(409, names.codeExists, `The short code "${row.code}" is already used. Choose a different code.`);
  }
}

const DISCIPLINE_NAMES: CatalogNames = {
  noun: 'discipline', invalid: HdcErrorCode.DisciplineInvalid, nameExists: HdcErrorCode.DisciplineNameExists, codeExists: HdcErrorCode.DisciplineCodeExists,
  isSystem: HdcErrorCode.DisciplineIsSystem,
};
const SERVICE_TYPE_NAMES: CatalogNames = {
  noun: 'service type', invalid: HdcErrorCode.ServiceTypeInvalid, nameExists: HdcErrorCode.ServiceTypeNameExists, codeExists: HdcErrorCode.ServiceTypeCodeExists,
  isSystem: HdcErrorCode.ServiceTypeIsSystem,
};

const slug = (code: string) => code.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function seedDisciplines(): Discipline[] {
  return [...SYSTEM_DISCIPLINES, ...SECTOR_DISCIPLINES].map(([code, name, description, industries]) => ({
    id: `disc-${slug(code)}`, code, name, description, industries: [...industries], isSystem: true, isActive: true, concurrencyVersion: 1,
  }));
}

function seedServiceTypes(): ServiceType[] {
  return [...SYSTEM_SERVICE_TYPES, ...SECTOR_SERVICE_TYPES].map(([code, name, description, industries, suggestedPayBasis]) => ({
    id: `svc-${slug(code)}`, code, name, description, industries: [...industries], isSystem: true, isActive: true, suggestedPayBasis, concurrencyVersion: 1,
  }));
}

export class MockHealthcareStore {
  disciplines: Discipline[] = seedDisciplines();
  serviceTypes: ServiceType[] = seedServiceTypes();
  industry: { industry: string | null; updatedAtUtc: string | null } = { industry: 'HomeHealth', updatedAtUtc: null };
  profile: CompanyIndustryProfile = initialProfile();
  companyRates: ServiceRate[] = [];

  /** The pay bases the mock "Payroll Calculation" can price (the real list comes from GET /api/service-lines/supported-pay-bases). A test may narrow it. */
  supportedPayBases: PayBasis[] = [...SERVICE_PAY_BASES];
  employeeRates: ServiceRate[] = [];
  private assignmentsByEmployee = new Map<string, DisciplineAssignment[]>();
  private allowedByEmployee = new Map<string, AllowedWorkType[]>();
  private ambiguousAdded = false;

  constructor() {
    this.companyRates = this.seedRates(SEED_COMPANY_RATES);
  }

  /** Test helper: the store is a module-level singleton, so one test's data must not leak into the next. */
  reset(): void {
    this.disciplines = seedDisciplines();
    this.serviceTypes = seedServiceTypes();
    this.industry = { industry: 'HomeHealth', updatedAtUtc: null };
    this.profile = initialProfile();
    this.supportedPayBases = [...SERVICE_PAY_BASES];
    this.employeeRates = [];
    this.assignmentsByEmployee = new Map();
    this.allowedByEmployee = new Map();
    this.ambiguousAdded = false;
    this.companyRates = this.seedRates(SEED_COMPANY_RATES);
  }

  // ---- seeding ----

  private seedRates(seeds: readonly SeedRate[]): ServiceRate[] {
    const rows: ServiceRate[] = [];
    for (const s of seeds) {
      const discipline = this.disciplines.find((d) => d.code === s.disciplineCode);
      const service = s.serviceTypeCode ? this.serviceTypes.find((t) => t.code === s.serviceTypeCode) : null;
      if (!discipline) continue;
      rows.push({
        id: s.id, disciplineId: discipline.id, disciplineName: discipline.name, serviceTypeId: service?.id ?? null, serviceTypeName: service?.name ?? null,
        workLocationId: null, payBasis: s.payBasis, rate: centsToWire(s.rateCents), currency: 'USD', differentialKind: s.differentialKind,
        differentialValue: s.differentialUnits === null ? null : scaled2ToWire(s.differentialUnits), description: SEED_RATE_DESCRIPTION(s.note),
        effectiveFrom: SEED_RATE_EFFECTIVE_FROM, effectiveTo: null, isActive: true, concurrencyVersion: 1, appliesToAnyWorkerCategory: false,
      });
    }
    return rows;
  }

  /** Adds the legacy duplicate rows the first time it is called (see AMBIGUOUS_SEED). */
  addAmbiguousRates(): void {
    if (this.ambiguousAdded) return;
    this.ambiguousAdded = true;
    this.companyRates = [...this.companyRates, ...this.seedRates(AMBIGUOUS_SEED)];
  }

  // ---- disciplines ----

  listDisciplines(params: { industry?: string | undefined; search?: string | undefined; includeInactive?: boolean | undefined } = {}): Discipline[] {
    return this.catalogList(this.disciplines, params);
  }

  getDiscipline(id: string): Discipline | undefined {
    return this.disciplines.find((d) => d.id === id);
  }

  createDiscipline(input: DisciplineInput): Discipline {
    validateCatalogInput(input, DISCIPLINE_NAMES);
    if (input.isActive) assertUnique(this.disciplines, input, DISCIPLINE_NAMES);
    const created: Discipline = {
      id: crypto.randomUUID(), code: normalizeCode(input.code), name: input.name.replace(/\s+/g, ' ').trim(), description: input.description.trim(),
      industries: [...input.industries], isSystem: false, isActive: input.isActive, concurrencyVersion: 1,
    };
    this.disciplines = [...this.disciplines, created];
    return created;
  }

  updateDiscipline(id: string, input: DisciplineInput, expected: number): Discipline {
    const row = this.requireEditable(this.disciplines, id, expected, DISCIPLINE_NAMES, 'discipline');
    validateCatalogInput(input, DISCIPLINE_NAMES);
    if (row.isActive) assertUnique(this.disciplines, input, DISCIPLINE_NAMES, id);
    const updated: Discipline = {
      ...row, code: normalizeCode(input.code), name: input.name.replace(/\s+/g, ' ').trim(), description: input.description.trim(),
      industries: [...input.industries], isActive: input.isActive, concurrencyVersion: row.concurrencyVersion + 1,
    };
    this.disciplines = this.disciplines.map((d) => (d.id === id ? updated : d));
    return updated;
  }

  setDisciplineActive(id: string, active: boolean, expected: number): Discipline {
    const row = this.requireEditable(this.disciplines, id, expected, DISCIPLINE_NAMES, 'discipline');
    if (active) assertUnique(this.disciplines, row, DISCIPLINE_NAMES, id);
    const updated: Discipline = { ...row, isActive: active, concurrencyVersion: row.concurrencyVersion + 1 };
    this.disciplines = this.disciplines.map((d) => (d.id === id ? updated : d));
    return updated;
  }

  // ---- assignments ----

  assignments(employeeId: string): DisciplineAssignment[] {
    return [...(this.assignmentsByEmployee.get(employeeId) ?? [])].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom) || Number(b.isPrimary) - Number(a.isPrimary));
  }

  activeAssignments(employeeId: string, asOf: string): DisciplineAssignment[] {
    return this.assignments(employeeId).filter((a) => a.effectiveFrom <= asOf && (a.effectiveTo === null || a.effectiveTo > asOf));
  }

  /** The employee's open primary assignment, if any. */
  primaryOf(employeeId: string): DisciplineAssignment | undefined {
    return this.assignments(employeeId).find((a) => a.isPrimary && a.effectiveTo === null);
  }

  assign(employeeId: string, input: CreateDisciplineAssignmentInput): DisciplineAssignment {
    const discipline = this.disciplines.find((d) => d.id === input.disciplineId);
    if (!discipline) throw problem(404, HdcErrorCode.DisciplineNotFound, 'Discipline not found.');
    if (!discipline.isActive) throw problem(409, HdcErrorCode.DisciplineInactive, 'That discipline is inactive and cannot be assigned. Reactivate it or choose another.');
    const from = input.effectiveFromDate || todayIso();
    if (!DATE_PATTERN.test(from)) throw problem(400, HdcErrorCode.AssignmentDateInvalid, 'The effective date is out of range.');
    const open = this.assignments(employeeId).filter((a) => a.effectiveTo === null);
    if (open.some((a) => a.disciplineId === input.disciplineId)) {
      throw problem(409, HdcErrorCode.AssignmentConflict, 'This employee already has this discipline assigned. End the current assignment first if it should change.');
    }
    let rows = this.assignmentsByEmployee.get(employeeId) ?? [];
    if (input.isPrimary) {
      // Changing the primary ends the old row and appends a new one.
      rows = rows.map((a) => (a.isPrimary && a.effectiveTo === null ? { ...a, effectiveTo: from } : a));
    }
    const created: DisciplineAssignment = {
      id: crypto.randomUUID(), disciplineId: discipline.id, disciplineCode: discipline.code, disciplineName: discipline.name,
      isPrimary: input.isPrimary, effectiveFrom: from, effectiveTo: null, reason: input.reason?.trim() || null,
    };
    this.assignmentsByEmployee.set(employeeId, [...rows, created]);
    return created;
  }

  endAssignment(employeeId: string, assignmentId: string, effectiveEndDate: string): DisciplineAssignment {
    const rows = this.assignmentsByEmployee.get(employeeId) ?? [];
    const row = rows.find((a) => a.id === assignmentId);
    if (!row) throw problem(404, HdcErrorCode.AssignmentNotFound, 'Discipline assignment not found.');
    if (row.effectiveTo !== null) throw problem(409, HdcErrorCode.AssignmentAlreadyEnded, 'This assignment has already ended.');
    // The end is exclusive: the first day the assignment no longer applies. The start date itself is allowed (it voids a mistaken row); an earlier date is not.
    if (!DATE_PATTERN.test(effectiveEndDate) || effectiveEndDate < row.effectiveFrom) {
      throw problem(400, HdcErrorCode.AssignmentDateInvalid, 'The end date cannot be before the start of the assignment.');
    }
    const ended = { ...row, effectiveTo: effectiveEndDate };
    this.assignmentsByEmployee.set(employeeId, rows.map((a) => (a.id === assignmentId ? ended : a)));
    return ended;
  }

  // ---- allowed work types (the mock stand-in for Employees' allowed-service-types; empty = no limit) ----

  allowedWorkTypes(employeeId: string): AllowedWorkTypes {
    const items = [...(this.allowedByEmployee.get(employeeId) ?? [])].sort((a, b) => a.name.localeCompare(b.name) || a.code.localeCompare(b.code));
    return { employeeId, isRestricted: items.length > 0, items };
  }

  /** The service's rules: the WHOLE set, no duplicates, at most 100; only NEWLY added work types are checked (they must exist and be active); the same set again changes nothing. */
  setAllowedWorkTypes(employeeId: string, serviceTypeIds: readonly string[]): AllowedWorkTypes {
    const invalid = (detail: string) => problem(400, HdcErrorCode.AllowedWorkTypesInvalid, detail);
    if (serviceTypeIds.length > MAX_ALLOWED_WORK_TYPES) throw invalid(`An employee can have at most ${MAX_ALLOWED_WORK_TYPES} allowed work types.`);
    if (new Set(serviceTypeIds).size !== serviceTypeIds.length) throw invalid('A work type was listed more than once.');
    if (serviceTypeIds.some((id) => !id)) throw invalid('A work type id is not valid.');
    const existing = this.allowedByEmployee.get(employeeId) ?? [];
    const kept = existing.filter((i) => serviceTypeIds.includes(i.serviceTypeId));
    const added: AllowedWorkType[] = [];
    for (const id of serviceTypeIds.filter((x) => !existing.some((i) => i.serviceTypeId === x))) {
      const type = this.serviceTypes.find((t) => t.id === id);
      if (!type) throw problem(404, HdcErrorCode.ServiceTypeNotFound, 'A work type you chose was not found.');
      if (!type.isActive) throw problem(409, HdcErrorCode.ServiceTypeInactive, `${type.name} is inactive. Reactivate it, or choose another work type.`);
      added.push({ serviceTypeId: type.id, code: type.code, name: type.name });
    }
    if (kept.length === existing.length && added.length === 0) return this.allowedWorkTypes(employeeId);
    this.allowedByEmployee.set(employeeId, [...kept, ...added]);
    return this.allowedWorkTypes(employeeId);
  }

  // ---- service types ----

  listServiceTypes(params: { industry?: string | undefined; search?: string | undefined; includeInactive?: boolean | undefined } = {}): ServiceType[] {
    return this.catalogList(this.serviceTypes, params);
  }

  getServiceType(id: string): ServiceType | undefined {
    return this.serviceTypes.find((t) => t.id === id);
  }

  createServiceType(input: ServiceTypeInput): ServiceType {
    validateCatalogInput(input, SERVICE_TYPE_NAMES);
    if (input.suggestedPayBasis === 'Salary') throw problem(400, HdcErrorCode.ServiceTypeInvalid, 'Salary cannot be the suggested pay basis for a service type.');
    if (input.isActive) assertUnique(this.serviceTypes, input, SERVICE_TYPE_NAMES);
    const created: ServiceType = {
      id: crypto.randomUUID(), code: normalizeCode(input.code), name: input.name.replace(/\s+/g, ' ').trim(), description: input.description.trim(),
      industries: [...input.industries], isSystem: false, isActive: input.isActive, suggestedPayBasis: input.suggestedPayBasis, concurrencyVersion: 1,
    };
    this.serviceTypes = [...this.serviceTypes, created];
    return created;
  }

  updateServiceType(id: string, input: ServiceTypeInput, expected: number): ServiceType {
    const row = this.requireEditable(this.serviceTypes, id, expected, SERVICE_TYPE_NAMES, 'service type');
    validateCatalogInput(input, SERVICE_TYPE_NAMES);
    if (input.suggestedPayBasis === 'Salary') throw problem(400, HdcErrorCode.ServiceTypeInvalid, 'Salary cannot be the suggested pay basis for a service type.');
    if (row.isActive) assertUnique(this.serviceTypes, input, SERVICE_TYPE_NAMES, id);
    const updated: ServiceType = {
      ...row, code: normalizeCode(input.code), name: input.name.replace(/\s+/g, ' ').trim(), description: input.description.trim(),
      industries: [...input.industries], suggestedPayBasis: input.suggestedPayBasis, isActive: input.isActive, concurrencyVersion: row.concurrencyVersion + 1,
    };
    this.serviceTypes = this.serviceTypes.map((t) => (t.id === id ? updated : t));
    return updated;
  }

  setServiceTypeActive(id: string, active: boolean, expected: number): ServiceType {
    const row = this.requireEditable(this.serviceTypes, id, expected, SERVICE_TYPE_NAMES, 'service type');
    if (active) assertUnique(this.serviceTypes, row, SERVICE_TYPE_NAMES, id);
    const updated: ServiceType = { ...row, isActive: active, concurrencyVersion: row.concurrencyVersion + 1 };
    this.serviceTypes = this.serviceTypes.map((t) => (t.id === id ? updated : t));
    return updated;
  }

  // ---- industry ----

  getIndustry(): CompanyIndustry {
    return { industry: this.industry.industry, options: FALLBACK_INDUSTRY_OPTIONS.map((o) => ({ ...o })), updatedAtUtc: this.industry.updatedAtUtc };
  }

  setIndustry(industry: string): CompanyIndustry {
    if (!KNOWN_INDUSTRIES.has(industry)) throw problem(400, 'INDUSTRY_INVALID', 'Choose an industry from the list.');
    this.industry = { industry, updatedAtUtc: new Date().toISOString() };
    return this.getIndustry();
  }

  // ---- industry profile and the NAICS reference (the mock holds a sample of the detailed industries) ----

  getProfile(): CompanyIndustryProfile {
    return JSON.parse(JSON.stringify(this.profile)) as CompanyIndustryProfile;
  }

  naicsSectors(): NaicsSector[] {
    return MOCK_NAICS_SECTORS.map(([code, title]) => ({ code, title }));
  }

  searchNaics(params: { sector?: string | undefined; q?: string | undefined; limit?: number | undefined }): NaicsIndustrySearch {
    if (params.sector && !MOCK_NAICS_SECTORS.some(([c]) => c === params.sector)) throw problem(400, 'INDUSTRY_INVALID', 'Choose one of the listed industry groups.');
    const limit = Math.min(Math.max(params.limit ?? 25, 1), 50);
    const words = (params.q ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    const sectorTitle = (code: string) => MOCK_NAICS_SECTORS.find(([c]) => c === code)?.[1] ?? code;
    const matches = MOCK_NAICS_INDUSTRIES
      .filter(([code, title, sector]) => (!params.sector || sector === params.sector) && words.every((w) => title.toLowerCase().includes(w) || code.startsWith(w)))
      .map(([code, title, sector]) => ({ code, title, sectorCode: sector, sectorTitle: sectorTitle(sector) }));
    return { version: MOCK_NAICS_VERSION, items: matches.slice(0, limit), hasMore: matches.length > limit };
  }

  setProfile(input: SetIndustryProfileInput): CompanyIndustryProfile {
    if (this.profile.source === 'Profile' && input.expectedConcurrencyVersion !== this.profile.concurrencyVersion) {
      throw problem(409, HdcErrorCode.ConcurrencyConflict, 'The industry was changed by someone else. Reload and try again.');
    }
    const { primary, additional, all, tags, isHealthcare } = this.evaluateAnswer(input);
    this.profile = {
      ...this.profile,
      primary, additional, needsReview: false, source: 'Profile', naicsVersion: all.some((s) => s.sectorCode) ? MOCK_NAICS_VERSION : null,
      isHealthcare, suggestionTags: tags,
      concurrencyVersion: this.profile.source === 'Profile' ? this.profile.concurrencyVersion + 1 : 1, updatedAtUtc: new Date().toISOString(),
    };
    return this.getProfile();
  }

  /** The service's setup review (rules version 1), read-only: where the industry comes from, the company's own work types, the company rates in effect today. Changes nothing. */
  setupReview(): CompensationSetupReview {
    const today = todayIso();
    const profile = this.profile;
    const primary = profile.primary;
    const label = primary?.healthcareTemplateLabel ?? primary?.detailTitle ?? primary?.sectorTitle ?? null;
    const healthcareTags = new Set([...FALLBACK_INDUSTRY_OPTIONS.filter((o) => o.code !== 'GeneralNonHealthcare').map((o) => o.code), `Naics${HEALTHCARE_SECTOR_CODE}`]);
    const legacy = this.industry.industry;
    const legacyLabel = FALLBACK_INDUSTRY_OPTIONS.find((o) => o.code === legacy)?.label ?? legacy;

    const own = this.serviceTypes.filter((t) => !t.isSystem && t.isActive);
    const tagged = own.filter((t) => t.industries.some((i) => healthcareTags.has(i))).map((t) => t.name);
    const untagged = own.filter((t) => t.industries.length === 0).map((t) => t.name);
    const inEffect = this.companyRates.filter((r) => r.effectiveFrom <= today && (r.effectiveTo === null || r.effectiveTo > today));
    const inactiveUsed = [...new Set(inEffect.map((r) => this.serviceTypes.find((t) => t.id === r.serviceTypeId)).filter((t) => t && !t.isActive).map((t) => t!.name))];

    const listed = (key: string, area: string, label2: string, severity: SetupReviewItem['severity'], names: string[], message: (n: number) => string): SetupReviewItem => ({
      key, area, label: label2, severity, message: message(names.length), count: names.length, names: [...names].sort((a, b) => a.localeCompare(b)).slice(0, 10),
    });
    const items: SetupReviewItem[] = [];
    if (profile.needsReview) {
      items.push({ key: 'industry', area: 'Industry', label: 'Industry', severity: 'Review', count: null, names: [], message: 'Your company was set up before industries were added, with “General (non-healthcare)”. EPN did not guess an industry for you. Choose the one that fits on Compensation Rates when you are ready; until then everything keeps working as it does today.' });
    } else if (profile.source === 'LegacySetting') {
      items.push({ key: 'industry', area: 'Industry', label: 'Industry', severity: 'Info', count: null, names: [], message: `Your industry is read from your original healthcare setting (${label ?? 'healthcare'}). It is not rewritten. Save your industry on Compensation Rates to record it against the current industry list; nothing changes until you do.` });
    } else if (profile.source === 'Profile' && primary?.kind === 'NotSure') {
      items.push({ key: 'industry', area: 'Industry', label: 'Industry', severity: 'Info', count: null, names: [], message: 'You answered “I’m not sure”. EPN suggests nothing and lists every default. You can add your own worker categories, work types and rates.' });
    } else if (profile.source === 'Profile') {
      items.push({ key: 'industry', area: 'Industry', label: 'Industry', severity: 'Ok', count: null, names: [], message: `Your industry is saved: ${label ?? 'an industry you described yourself'}. It only decides which defaults are listed first.` });
    } else {
      items.push({ key: 'industry', area: 'Industry', label: 'Industry', severity: 'Info', count: null, names: [], message: 'No industry has been chosen yet. EPN suggests nothing and lists every default until you choose one. Nothing else is affected.' });
    }
    const legacyHealthcare = legacy !== null && legacy !== 'GeneralNonHealthcare';
    const stale = profile.source === 'Profile' && legacy !== null
      && (legacyHealthcare ? !(primary?.sectorCode === HEALTHCARE_SECTOR_CODE && primary?.healthcareTemplate?.toLowerCase() === legacy.toLowerCase()) : profile.isHealthcare);
    if (stale) {
      items.push({ key: 'industry-original-setting', area: 'Industry', label: 'The original healthcare setting is no longer used', severity: 'Info', count: null, names: [], message: `Your original healthcare setting (${legacyLabel}) is still stored, but it is no longer used for suggestions now that you have saved an industry. It changes nothing and nothing was rewritten.` });
    }
    if (!profile.isHealthcare && tagged.length > 0) {
      items.push(listed('work-types-healthcare-tag', 'Work types', 'Some of your own work types are tagged for healthcare', 'Info', tagged, (n) => `${n} of your own work types carry a healthcare industry tag although your industry is not Health Care and Social Assistance. That only affects what is listed first: they are always shown, and nothing needs to change.`));
    }
    if (untagged.length > 0) {
      items.push(listed('work-types-no-industry', 'Work types', 'Some of your own work types carry no industry', 'Info', untagged, (n) => `${n} of your own work types carry no industry. They are always shown, whatever your industry is.`));
    }
    if (inactiveUsed.length > 0) {
      items.push(listed('rates-on-inactive-work-type', 'Company rates', 'Active rates point at a work type that is no longer active', 'Review', inactiveUsed, (n) => `Active company rates point at ${n} work types that are no longer active. A pay-run line that names such a work type is refused, so those rates cannot be used. Reactivate the work type, or end the rates.`));
    }
    if (inEffect.length === 0) {
      items.push({ key: 'rates-none', area: 'Company rates', label: 'No company rates are in effect', severity: 'Info', count: 0, names: [], message: 'No company rate is in effect today. A pay-run service line can then be priced only from an employee\'s own rate or base rate. Add a company rate on Compensation Rates when you want one.' });
    }

    const counts = new Map<string, number>();
    for (const r of inEffect) counts.set(r.payBasis, (counts.get(r.payBasis) ?? 0) + 1);
    const byPayBasis = [...counts].map(([payBasis, count]) => ({ payBasis, count })).sort((a, b) => b.count - a.count || a.payBasis.localeCompare(b.payBasis));
    return {
      rulesVersion: 1, asOf: today,
      industry: { source: profile.source, needsReview: profile.needsReview, isHealthcare: profile.isHealthcare, label },
      workTypes: {
        systemActive: this.serviceTypes.filter((t) => t.isSystem && t.isActive).length, systemInactive: this.serviceTypes.filter((t) => t.isSystem && !t.isActive).length,
        companyActive: this.serviceTypes.filter((t) => !t.isSystem && t.isActive).length, companyInactive: this.serviceTypes.filter((t) => !t.isSystem && !t.isActive).length,
      },
      companyRates: { active: inEffect.length, byPayBasis, beyondOriginalFive: byPayBasis.filter((b) => !(ORIGINAL_SERVICE_PAY_BASES as readonly string[]).includes(b.payBasis)).reduce((n, b) => n + b.count, 0) },
      items,
      summary: { review: items.filter((i) => i.severity === 'Review').length, info: items.filter((i) => i.severity === 'Info').length, ok: items.filter((i) => i.severity === 'Ok').length },
      notIncluded: [
        { area: 'Worker categories', label: "The company's worker categories", whereToLook: 'Settings, Payroll, Compensation Rates, Worker categories (they are kept by Employees, so this review does not count them).' },
        { area: 'Employee assignments', label: 'Which worker categories each employee holds', whereToLook: "Each employee's Payroll Details, Worker category and rates." },
        { area: 'Employee rates', label: 'Rates set for one employee', whereToLook: "Each employee's Payroll Details, Service rates for this employee (they are kept by Compensation)." },
        { area: 'Payroll history', label: 'Past pay-run lines and approved payrolls', whereToLook: 'They keep the words and rates they were saved with and never change; open the payroll to see them.' },
      ],
    };
  }

  /** The service's preview: the SAME validation and tag rules as a save, the concurrency version ignored, and nothing is saved. */
  previewProfile(input: SetIndustryProfileInput): IndustryChangePreview {
    const proposed = this.evaluateAnswer(input);
    const current = this.profile.suggestionTags;
    const added = proposed.tags.filter((t) => !current.includes(t));
    const removed = current.filter((t) => !proposed.tags.includes(t));
    return {
      currentSuggestionTags: [...current], currentIsHealthcare: this.profile.isHealthcare, proposedSuggestionTags: proposed.tags, proposedIsHealthcare: proposed.isHealthcare,
      addedSuggestionTags: added, removedSuggestionTags: removed, suggestionsChange: added.length > 0 || removed.length > 0,
      naicsVersion: proposed.all.some((s) => s.sectorCode) ? MOCK_NAICS_VERSION : null,
    };
  }

  /** Validates an answer and works out its tags (shared by a save and a preview, like the service). */
  private evaluateAnswer(input: SetIndustryProfileInput) {
    const fail = (detail: string) => problem(400, 'INDUSTRY_INVALID', detail);
    if (input.additional.length > MAX_ADDITIONAL_INDUSTRIES) throw fail(`A company can add at most ${MAX_ADDITIONAL_INDUSTRIES} additional industries.`);

    const normalize = (s: SetIndustryProfileInput['primary'], allowNotSure: boolean): IndustrySelection => {
      const kinds: readonly string[] = ['Chosen', 'NotSure', 'Custom'];
      if (!kinds.includes(s.kind)) throw fail('Choose an industry, "I\'m not sure" or "Other or custom industry".');
      if (s.kind === 'NotSure') {
        if (!allowNotSure) throw fail('An additional industry must be an industry or a custom description.');
        return { kind: 'NotSure', sectorCode: null, sectorTitle: null, detailCode: null, detailTitle: null, customDescription: null, healthcareTemplate: null, healthcareTemplateLabel: null };
      }
      const description = (s.customDescription ?? '').replace(/\s+/g, ' ').trim();
      if (description.length > INDUSTRY_DESCRIPTION_MAX) throw fail(`Describe your industry in ${INDUSTRY_DESCRIPTION_MAX} characters or fewer.`);
      const sector = s.sectorCode ? MOCK_NAICS_SECTORS.find(([c]) => c === s.sectorCode) : undefined;
      if (s.sectorCode && !sector) throw fail('Choose one of the listed industry groups.');
      if (s.kind === 'Chosen' && !sector) throw fail('Choose an industry group.');
      if (s.kind === 'Custom' && !description) throw fail('Describe your industry in your own words.');
      let detail: (typeof MOCK_NAICS_INDUSTRIES)[number] | undefined;
      if (s.detailCode) {
        if (!sector) throw fail('Choose an industry group before a detailed industry.');
        detail = MOCK_NAICS_INDUSTRIES.find(([code, , sec]) => code === s.detailCode && sec === sector[0]);
        if (!detail) throw fail('That detailed industry is not in the chosen industry group.');
      }
      const template = s.healthcareTemplate ? FALLBACK_INDUSTRY_OPTIONS.find((o) => o.code === s.healthcareTemplate && o.code !== 'GeneralNonHealthcare') : undefined;
      if (s.healthcareTemplate && (!template || sector?.[0] !== HEALTHCARE_SECTOR_CODE)) throw fail('The healthcare type does not apply to this industry group.');
      return {
        kind: s.kind, sectorCode: sector?.[0] ?? null, sectorTitle: sector?.[1] ?? null, detailCode: detail?.[0] ?? null, detailTitle: detail?.[1] ?? null,
        customDescription: description || null, healthcareTemplate: template?.code ?? null, healthcareTemplateLabel: template?.label ?? null,
      };
    };

    const primary = normalize(input.primary, true);
    const additional = input.additional.map((a) => normalize(a, false));
    const key = (s: IndustrySelection) => `${s.kind}|${s.sectorCode}|${s.detailCode}|${(s.customDescription ?? '').toUpperCase()}|${s.healthcareTemplate}`;
    const seen = new Set([key(primary)]);
    for (const a of additional) {
      if (seen.has(key(a))) throw fail('The same industry is listed more than once.');
      seen.add(key(a));
    }

    const tagsFor = (s: IndustrySelection): string[] => {
      if (s.kind === 'NotSure' || !s.sectorCode) return [];
      const tags: string[] = [];
      if (s.sectorCode === HEALTHCARE_SECTOR_CODE) tags.push(...(s.healthcareTemplate ? [s.healthcareTemplate] : this.profile.healthcareTemplates.map((t) => t.code)));
      tags.push(`Naics${s.sectorCode}`);
      return tags;
    };
    const all = [primary, ...additional];
    return { primary, additional, all, tags: [...new Set(all.flatMap(tagsFor))], isHealthcare: all.some((s) => s.sectorCode === HEALTHCARE_SECTOR_CODE) };
  }

  // ---- rates (company and employee) ----

  listRates(
    rows: ServiceRate[],
    params: { disciplineId?: string | undefined; serviceTypeId?: string | undefined; workLocationId?: string | undefined; payBasis?: string | undefined; asOf?: string | undefined; includeEnded?: boolean | undefined } = {},
  ): ServiceRate[] {
    const asOf = params.asOf;
    return rows
      // A company rate for ANY worker category also applies to the category asked for, like the real service.
      .filter((r) => (!params.disciplineId || r.disciplineId === params.disciplineId || r.disciplineId === null)
        && (!params.serviceTypeId || r.serviceTypeId === params.serviceTypeId)
        && (!params.workLocationId || r.workLocationId === params.workLocationId)
        && (!params.payBasis || r.payBasis === params.payBasis)
        && (asOf ? r.effectiveFrom <= asOf && (r.effectiveTo === null || r.effectiveTo > asOf) : params.includeEnded || r.effectiveTo === null || r.effectiveTo > todayIso()))
      .sort((a, b) => (a.disciplineName ?? '').localeCompare(b.disciplineName ?? '') || (a.serviceTypeName ?? '').localeCompare(b.serviceTypeName ?? '') || a.effectiveFrom.localeCompare(b.effectiveFrom));
  }

  private buildRate(input: CreateServiceRateInput, employeeId?: string): ServiceRate {
    const fail = (detail: string) => problem(400, employeeId ? HdcErrorCode.EmployeeRateInvalid : HdcErrorCode.RateInvalid, detail);
    // A company rate for ANY worker category is chosen explicitly (never inferred from a missing category); an employee rate always names one.
    const anyCategory = input.appliesToAnyWorkerCategory === true;
    if (anyCategory && (employeeId || input.disciplineId !== null)) throw fail('A rate for any worker category must not name a worker category, and only a company rate can apply to any category.');
    if (!anyCategory && !input.disciplineId) throw fail('Choose a worker category, or choose “any worker category”.');
    const discipline = anyCategory ? null : this.disciplines.find((d) => d.id === input.disciplineId);
    if (!anyCategory && (!discipline || !discipline.isActive)) throw problem(400, HdcErrorCode.DisciplineNotFound, 'The specified discipline does not exist in your company.');
    const service = input.serviceTypeId ? this.serviceTypes.find((t) => t.id === input.serviceTypeId) : null;
    if (input.serviceTypeId && (!service || !service.isActive)) throw problem(400, HdcErrorCode.ServiceTypeNotFound, 'The specified service type does not exist in your company.');

    if (!SERVICE_PAY_BASES.includes(input.payBasis)) throw fail('Salary is not a pay basis for a service rate. Salary stays on the employee’s base pay.');
    if (!ORIGINAL_SERVICE_PAY_BASES.includes(input.payBasis) && !this.supportedPayBases.includes(input.payBasis)) {
      throw problem(409, HdcErrorCode.PayBasisNotSupported, `Payroll cannot price the pay basis ${input.payBasis} yet, so it cannot be saved. Choose another pay basis, or try again once payroll has been updated.`);
    }
    if (!Number.isInteger(input.rateCents) || input.rateCents < 0 || input.rateCents > 100_000_000) throw fail('The rate must be between 0 and 1,000,000.');
    if (input.differentialKind === 'Percent' && (input.differentialUnits === null || input.differentialUnits < 0 || input.differentialUnits > 100_000)) throw fail('The differential percent must be between 0 and 1,000.');
    if (input.differentialKind === 'Amount' && (input.differentialUnits === null || input.differentialUnits < 0 || input.differentialUnits > 100_000_000)) throw fail('The differential amount must be between 0 and 1,000,000.');
    if ((input.description ?? '').length > 300) throw fail('The description must be 300 characters or fewer.');
    if (!DATE_PATTERN.test(input.effectiveFrom)) throw fail('Enter the date the rate starts.');
    if (input.effectiveTo !== null && (!DATE_PATTERN.test(input.effectiveTo) || input.effectiveTo <= input.effectiveFrom)) throw fail('The end date must be after the start date.');
    return {
      id: crypto.randomUUID(), ...(employeeId ? { employeeId } : {}), disciplineId: discipline?.id ?? null, disciplineName: discipline?.name ?? null,
      serviceTypeId: service?.id ?? null, serviceTypeName: service?.name ?? null, workLocationId: input.workLocationId, payBasis: input.payBasis,
      rate: centsToWire(input.rateCents), currency: 'USD', differentialKind: input.differentialKind,
      differentialValue: input.differentialKind === 'None' || input.differentialUnits === null ? null : scaled2ToWire(input.differentialUnits),
      description: input.description.trim(), effectiveFrom: input.effectiveFrom, effectiveTo: input.effectiveTo, isActive: input.effectiveTo === null, concurrencyVersion: 1,
      appliesToAnyWorkerCategory: anyCategory,
    };
  }

  private assertNoOverlap(rows: ServiceRate[], created: ServiceRate): void {
    const clash = rows.find(
      // A voided row (ended on its own start date) never applied, so it never overlaps anything.
      (r) => (r.effectiveTo === null || r.effectiveTo > r.effectiveFrom) && r.disciplineId === created.disciplineId && r.serviceTypeId === created.serviceTypeId && r.workLocationId === created.workLocationId
        && r.payBasis === created.payBasis && (r.employeeId ?? null) === (created.employeeId ?? null) && rangesOverlap(r, created),
    );
    if (clash) {
      throw problem(
        409, HdcErrorCode.RateOverlap,
        `This rate overlaps an existing rate for the same discipline, service type, location and pay basis (row ${clash.id}, from ${clash.effectiveFrom}${clash.effectiveTo ? ` to ${clash.effectiveTo}` : ', still in effect'}). End that rate first, or change the dates.`,
        { conflictingRateId: clash.id },
      );
    }
  }

  createCompanyRate(input: CreateServiceRateInput): ServiceRate {
    const created = this.buildRate(input);
    this.assertNoOverlap(this.companyRates, created);
    this.companyRates = [...this.companyRates, created];
    return created;
  }

  createEmployeeRate(employeeId: string, input: CreateServiceRateInput): ServiceRate {
    const created = this.buildRate(input, employeeId);
    this.assertNoOverlap(this.employeeRates, created);
    this.employeeRates = [...this.employeeRates, created];
    return created;
  }

  employeeRatesOf(employeeId: string): ServiceRate[] {
    return this.employeeRates.filter((r) => r.employeeId === employeeId);
  }

  endCompanyRate(id: string, effectiveTo: string, expected?: number): ServiceRate {
    const row = this.companyRates.find((r) => r.id === id);
    if (!row) throw problem(404, HdcErrorCode.ServiceRateNotFound, 'Rate not found.');
    const ended = this.ended(row, effectiveTo, expected);
    this.companyRates = this.companyRates.map((r) => (r.id === id ? ended : r));
    return ended;
  }

  endEmployeeRate(employeeId: string, id: string, effectiveTo: string, expected?: number): ServiceRate {
    const row = this.employeeRates.find((r) => r.id === id && r.employeeId === employeeId);
    if (!row) throw problem(404, HdcErrorCode.EmployeeRateNotFound, 'The service rate does not exist in your company.');
    const ended = this.ended(row, effectiveTo, expected);
    this.employeeRates = this.employeeRates.map((r) => (r.id === id ? ended : r));
    return ended;
  }

  private ended(row: ServiceRate, effectiveTo: string, expected?: number): ServiceRate {
    if (expected !== undefined && expected !== row.concurrencyVersion) {
      throw problem(409, HdcErrorCode.ConcurrencyConflict, 'This rate was changed by someone else. Reload it and try again.');
    }
    if (row.effectiveTo !== null) throw problem(409, HdcErrorCode.RateAlreadyEnded, 'This rate already has an end date. A rate can be ended only once; add a new rate for any later change.');
    // Exclusive end: the first day the rate no longer applies. Its own start date voids a mistaken row; an earlier date is refused.
    if (!DATE_PATTERN.test(effectiveTo) || effectiveTo < row.effectiveFrom) throw problem(400, HdcErrorCode.RateInvalid, 'The end date cannot be before the start of the rate.');
    return { ...row, effectiveTo, isActive: false, concurrencyVersion: row.concurrencyVersion + 1 };
  }

  // ---- shared catalog helpers ----

  private catalogList<T extends CatalogRow>(rows: T[], params: { industry?: string | undefined; search?: string | undefined; includeInactive?: boolean | undefined }): T[] {
    const q = (params.search ?? '').trim().toLowerCase();
    return rows
      .filter((r) => (params.includeInactive ? true : r.isActive)
        && (!params.industry || r.industries.includes(params.industry) || !r.isSystem)
        && (!q || r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q) || r.description.toLowerCase().includes(q)))
      .sort((a, b) => Number(b.isSystem) - Number(a.isSystem) || a.name.localeCompare(b.name));
  }

  private requireEditable<T extends CatalogRow>(rows: T[], id: string, expected: number, names: CatalogNames, noun: string): T {
    const row = rows.find((r) => r.id === id);
    if (!row) throw new ApiError(404, `That ${noun} does not exist in your company.`, null, true, null);
    if (row.isSystem) throw problem(409, names.isSystem, `This is an EPN default ${noun} and cannot be changed. Add your own ${noun} instead.`);
    if (row.concurrencyVersion !== expected) {
      throw problem(409, HdcErrorCode.ConcurrencyConflict, `This ${noun} was changed by someone else after you opened it. Reload it, then make your change again.`);
    }
    return row;
  }
}

export const mockHealthcareStore = new MockHealthcareStore();
